/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-11-11 17:12:31
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */

import mergeWith from "lodash-es/mergeWith";
import isEqual from "lodash-es/isEqual";
import { nanoid } from "nanoid";
import { z } from "zod";
import { EventEmitter } from "eventemitter3";

import { Environments } from "../utilities";
import Utility from "@/frameworks/libraries/utilities/utility";
import { LocalData } from "../storage";

const LOGTAG = "#Properties#";
const UniqueID = nanoid(64);

/**
 * A class designed to manage and emit changes for a set of properties.
 *
 * @template T
 */
class PropertiesStatic<T = any> extends EventEmitter {
	private static _instance: PropertiesStatic<any>;
	private _property: Record<string, T> = {};
	private _browserTabID = this.read_old_tab_id();
	private _finger_print = this.read_old_finger_print();

	constructor() {
		super();
	}

	public static getInstance(): PropertiesStatic<any> {
		if (!PropertiesStatic._instance) {
			PropertiesStatic._instance = new PropertiesStatic();
		}
		return PropertiesStatic._instance;
	}
	/**
	 * Initializes the fingerprint if it has not been set. This method checks if the current
	 * fingerprint array is empty, and if so, it assigns a new fingerprint obtained from
	 * the Utility.fingerprint method. It logs the process for debugging purposes.
	 *
	 * @return {Promise<void>} A promise that resolves once the fingerprint initialization is complete.
	 */
	public async init_finger_print(): Promise<void> {
		if (this.finger_print.length <= 0) {
			this._finger_print = await Utility.fingerprint();
			console.debug(LOGTAG, "init_finger_print", this.finger_print);
		}
	}

	/**
	 * Disposes of the current instance, ensuring that local data is properly set before disposal.
	 * Specifically, it saves the browser tab identifier into local storage.
	 *
	 * @return {void} This method does not return any value.
	 */
	public dispose(): void {
		LocalData.set("btid", this.browserTabID);
		this.finger_print.length > 0 && LocalData.set("fpid", this.finger_print);
	}

	/**
	 * Retrieves the unique identifier of the current browser tab.
	 *
	 * @return {string} The unique identifier of the browser tab.
	 */
	public get browserTabID(): string {
		return this._browserTabID;
	}

	/**
	 * Retrieves the fingerprint value associated with the current object.
	 *
	 * @return {string} The fingerprint of the object.
	 */
	public get finger_print(): string {
		return this._finger_print;
	}

	/**
	 * Sets the finger print value.
	 *
	 * @param {string} value - The new value to be assigned to the finger print.
	 */
	public set finger_print(value: string | null) {
		if (value) {
			this._finger_print = value;
		} else {
			this._finger_print = "";
		}
	}

	/**
	 * Retrieves the value of the private `_property` field.
	 *
	 * @return {T} The current value of `_property`.
	 */
	public get property(): T {
		return this._property as T;
	}

	/**
	 * Sets the property value with the provided partial object. Merges the current
	 * property values with the new values. If the value is an array, it concatenates
	 * the arrays or sets it to null if the new value is null.
	 *
	 * @param {Partial<T>} _value - The partial object containing new property values.
	 */
	public set property(_value: Partial<T>) {
		const value = _value;
		for (const [key, data] of Object.entries(value)) {
			if (data instanceof URL) {
				if ((this._property[key] as URL).toString() !== data.toString()) {
					break;
				}
			} else if (!isEqual(this._property[key], data)) {
				break;
			}
		}

		this._property = mergeWith(this._property, value, (objValue, srcValue) => {
			if (Array.isArray(objValue)) {
				if (srcValue === null) {
					objValue = null;
					return objValue;
				}
				return objValue.concat(srcValue);
			}
		});

		this.emit("property_changed", this._property);
	}

	/**
	 * Reads the old tab ID stored in local data under the key "btid", removes this entry from local data,
	 * and returns the tab ID as a string.
	 *
	 * @return {string} The old tab ID stored in local data.
	 * @private
	 */
	private read_old_tab_id(): string {
		const tab_id = LocalData.get("btid", UniqueID) as string;
		LocalData.remove("btid");
		return tab_id;
	}

	/**
	 * Reads the old fingerprint from local storage using a key "fpid". If the key does not exist,
	 * it falls back to generating a new fingerprint using the utility function `Utility.fingerprint()`.
	 * After obtaining the fingerprint, it removes any entry with the key "finger_print" from local storage.
	 *
	 * @return A promise that resolves to a string containing the fingerprint.
	 */
	private read_old_finger_print(): string {
		const finger_print = LocalData.get("fpid", "") as string;
		LocalData.remove("finger_print");
		return finger_print;
	}

	/**
	 * Retrieves the current environment mode indicating whether it is development mode.
	 *
	 * @return {boolean} Returns true if the environment is in development mode, false otherwise.
	 */
	public get isDevelopment(): boolean {
		const developmentMode = Environments.env<boolean>("DEVELOPMENT_MODE", false);
		return z.coerce.boolean().parse(developmentMode);
	}

	/**
	 * Retrieves the current product mode from the environment variables.
	 *
	 * @return {string} The product mode as a string.
	 */
	public get productMode(): string {
		return Environments.env<string>("PRODUCT_MODE");
	}

	/**
	 * Checks if the solution is non-empty and not equal to "SaaS".
	 *
	 * @return {boolean} True if the solution meets the criteria, otherwise false.
	 */
	public get isSolution(): boolean {
		return (this.solution.length > 0 && this.solution !== "SaaS") || false;
	}

	/**
	 * Retrieves the "SOLUTION" environment variable.
	 *
	 * @return {string} The value of the "SOLUTION" environment variable.
	 */
	public get solution(): string {
		return Environments.env<string>("SOLUTION");
	}

	/**
	 * Retrieves the host name of the media server from the environment variables.
	 *
	 * @return {string} The media server host name.
	 */
	public get mediaServerHostName(): string {
		return Environments.env<string>("MEDIASERVER_HOSTNAME");
	}

	/**
	 * Retrieves an array of ICE server configurations used for establishing a WebRTC connection.
	 *
	 * @return {Array<{ urls: string; username: string; credential: string }>} An array containing ICE server configuration objects, each including the server URL, username, and credential.
	 */
	public get iceServers(): Array<{ urls: string; username: string; credential: string }> {
		const iceServerHostName = Environments.env<string>("ICESERVER_HOSTNAME");
		const iceServer = {
			urls: `turn:${iceServerHostName}`,
			username: "test",
			credential: "test"
		};

		return [iceServer];
	}
}

const Properties = PropertiesStatic.getInstance();
export default Properties;
